import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useParams } from "react-router-dom";
import { CartProvider } from "./lib/cartContext";
import { AuthProvider } from "./lib/AuthContext";
import { SavedProvider } from "./lib/SavedContext";
import { Navigation } from "./components/layout/Navigation";
import { Footer } from "./components/layout/Footer";
import { RouteScrollToTop } from "./components/layout/RouteScrollToTop";
import { useAffiliateClickTracking } from "./lib/useAffiliateClickTracking";
import { CartDrawer } from "./components/cart/CartDrawer";
import { LastOrderPointsBanner } from "./components/rewards/LastOrderPointsBanner";
import { LeadCaptureForm } from "./components/forms/LeadCaptureForm";
import { AIChatWidget } from "./components/chat/AIChatWidget";
import { useCanonicalUrl, HomeFAQSchema } from "./components/seo/SchemaMarkup";
import RecentlyViewedProductsCarousel from "./components/shop/RecentlyViewedProducts";
import { healthApi, articlesApi } from "./lib/api";
import { ArrowRight, Zap, Brain, Timer, Dumbbell, FlaskConical, Clock, TrendingUp, Flame, BookOpen, ChevronRight } from "lucide-react";
import { useReadingProgress } from "./lib/useReadingProgress";
import { trackPageView, initAffiliateClickTracker } from "./lib/analytics";
import JustPublishedStrip from "./components/hero/JustPublishedStrip";
import EditorsPicksStrip from "./components/hero/EditorsPicksStrip";
import MerchShowcase from "./components/hero/MerchShowcase";
import PeptiQPopupGate from "./components/PeptiQPopupGate";

// Import Pages
import BlogPage from "./pages/BlogPage";
import ArticlePage from "./pages/ArticlePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import TimersPage from "./pages/TimersPage";
import NotFoundPage from "./pages/NotFoundPage";
import HyroxPage from "./pages/HyroxPage";
import ContactPage from "./pages/ContactPage";
import AffiliatePage from "./pages/AffiliatePage";
import PeptideCalculatorPage from "./pages/PeptideCalculatorPage";
import ToolsPage from "./pages/ToolsPage";
import ProteinCalculatorPage from "./pages/ProteinCalculatorPage";
import TDEECalculatorPage from "./pages/TDEECalculatorPage";
import MacroCalculatorPage from "./pages/MacroCalculatorPage";
import OneRepMaxPage from "./pages/OneRepMaxPage";
import BodyFatCalculatorPage from "./pages/BodyFatCalculatorPage";
import HeartRateZonesPage from "./pages/HeartRateZonesPage";
import MyResultsPage from "./pages/MyResultsPage";
import AboutPage from "./pages/AboutPage";
import HyroxFinishTimePage from "./pages/HyroxFinishTimePage";
import HyroxBenchmarkReport from "./pages/HyroxBenchmarkReport";
import HyroxNearMePage from "./pages/HyroxNearMePage";
import HyroxGymsHubPage from "./pages/HyroxGymsHubPage";
import GoRedirect from "./pages/GoRedirect";
import RunClubBadgePage from "./pages/RunClubBadgePage";
import GymPreviewPage from "./pages/GymPreviewPage";
import PartnerKitPage from "./pages/PartnerKitPage";
import CitySeriesPage from "./pages/CitySeriesPage";
import WomensCollectionPage from "./pages/WomensCollectionPage";
import RecoveryCalculatorPage from "./pages/RecoveryCalculatorPage";
import PeptideCheatSheetPage from "./pages/PeptideCheatSheetPage";
import PeptideDosingCalculatorPage from "./pages/PeptideDosingCalculatorPage";
import StationSplitCalculatorPage from "./pages/StationSplitCalculatorPage";
import HydrotherapyPage from "./pages/HydrotherapyPage";
import RecoveryStackPage from "./pages/RecoveryStackPage";
import ReconstitutionGuidePage from "./pages/ReconstitutionGuidePage";
import BodyRecompPage from "./pages/BodyRecompPage";
import PeptideCycleTrackerPage from "./pages/PeptideCycleTrackerPage";
import PeptideStackBuilderPage from "./pages/PeptideStackBuilderPage";
import PeptideComparisonToolPage from "./pages/PeptideComparisonToolPage";
import BiohackingHubPage from "./pages/BiohackingHubPage";
import InsightsPage from "./pages/InsightsPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import BiohackingScorePage from "./pages/BiohackingScorePage";
import LongevityScorePage from "./pages/LongevityScorePage";
import PeptidesHubPage from "./pages/PeptidesHubPage";
import RecoveryHubPage from "./pages/RecoveryHubPage";
import LongevityHubPage from "./pages/LongevityHubPage";
import AdminDashboard from "./pages/AdminDashboard";
import GymUploadPage from "./pages/GymUploadPage";
import LocatorHub from "./pages/locator/LocatorHub";
import LocatorDirectory from "./pages/locator/LocatorDirectory";
import LocatorTherapyHub from "./pages/locator/LocatorTherapyHub";
import LocatorTherapyMetro from "./pages/locator/LocatorTherapyMetro";
import LocatorSubmit from "./pages/locator/LocatorSubmit";
import LocatorClaim from "./pages/locator/LocatorClaim";
import LocatorNearMe from "./pages/locator/LocatorNearMe";
import LocatorFacilityProfile from "./pages/locator/LocatorFacilityProfile";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import AccountPage from "./pages/AccountPage";
import SharedRacePlanPage from "./pages/SharedRacePlanPage";
import HyroxContentPage from "./pages/HyroxContentPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import ReturnsPage from "./pages/ReturnsPage";
import ShippingPage from "./pages/ShippingPage";
import FAQPage from "./pages/FAQPage";
import AuthorPage from "./pages/AuthorPage";
import EditorialPolicyPage from "./pages/EditorialPolicyPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import WishlistPage from "./pages/WishlistPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import ProtocolTrackerPage from "./pages/ProtocolTrackerPage";
import GuidesHubPage from "./pages/GuidesHubPage";
import MealPlannerPage from "./pages/MealPlannerPage";
import MacroTrackerPage from "./pages/MacroTrackerPage";
import AffiliatePortalPage from "./pages/AffiliatePortalPage";
import APRPromoPopupGate from "./components/APRPromoPopupGate";
import CommunityPage from "./pages/CommunityPage";
import CommunityProfilePage from "./pages/CommunityProfilePage";
import ChallengesPage from "./pages/ChallengesPage";
import ChallengeDetailPage from "./pages/ChallengeDetailPage";
import RewardsPage from "./pages/RewardsPage";
import AffiliateLeaderboardPage from "./pages/AffiliateLeaderboardPage";
import GymDirectoryPage from "./pages/GymDirectoryPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import BrandGuidelinesPage from "./pages/BrandGuidelinesPage";
import SeriesPage from "./pages/SeriesPage";
import PeptideSubscriptionPage from "./pages/peptide/PeptideSubscriptionPage";
import PromotionTermsGiftPage from "./pages/peptide/PromotionTermsGiftPage";
import BiohackerGiftRedemptionPage from "./pages/peptide/BiohackerGiftRedemptionPage";
import BiohackerWalletPage from "./pages/peptide/BiohackerWalletPage";
import GearBoxPage from "./pages/GearBoxPage";
import GearBoxSuccessPage from "./pages/GearBoxSuccessPage";

const LOGO_URL = "/ninjathlete-wordmark.png";
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_1590f9cf-5e05-4494-9fd1-928104d1fc8f/artifacts/q82791ue_CHICKEN%20DINNER.mp4";

import { RecommendedForYou } from "./components/RecommendedForYou";

import { HeroRedesign } from "./components/home/HeroRedesign";
import { TrustBar } from "./components/home/TrustBar";
import { ThreePathsSection } from "./components/home/ThreePathsSection";
import { LeadMagnetCapture } from "./components/home/LeadMagnetCapture";
import { ThreeForSeventyFive } from "./components/home/ThreeForSeventyFive";
import { FeaturedGuideBlock } from "./components/home/FeaturedGuideBlock";
import { ToolsStrip } from "./components/home/ToolsStrip";
import { ExitIntentModal } from "./components/dojo/ExitIntentModal";
import { SoftLeadMagnetGate } from "./components/dojo/SoftLeadMagnetGate";
import { Toaster as SonnerToaster } from "sonner";



// Peptide Guides Section
const PeptideGuidesSection = () => {
  const [peptideArticles, setPeptideArticles] = useState([]);

  useEffect(() => {
    const fetchPeptides = async () => {
      try {
        const res = await articlesApi.getAll({ category: 'Peptides', limit: 8 });
        setPeptideArticles(res.data);
      } catch (e) { console.error(e); }
    };
    fetchPeptides();
  }, []);

  if (peptideArticles.length === 0) return null;

  return (
    <section className="section-spacing bg-[#050505]" data-testid="peptide-guides-section">
      <div className="container-ninja">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="text-ninja-label text-[#E63946] mb-3 flex items-center gap-2">
              <FlaskConical className="w-4 h-4" /> Peptide Research
            </p>
            <h2 className="text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              The Complete Peptide Guides
            </h2>
          </div>
          <Link to="/blog?category=Peptides" className="btn-stealth text-sm hidden sm:inline-flex items-center gap-2">
            View All Guides <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {peptideArticles.slice(0, 8).map((article) => (
            <Link
              key={article.slug}
              to={`/blogs/article/${article.slug}`}
              className="group block bg-zinc-900/50 border border-zinc-800 hover:border-[#E63946]/40 transition-all duration-300 overflow-hidden"
              data-testid={`peptide-card-${article.slug}`}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={article.featured_image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <span className="text-[10px] uppercase tracking-wider text-[#E63946] font-bold">
                  {article.category}
                </span>
                <h4 className="text-white text-sm font-medium mt-1 mb-2 line-clamp-2 group-hover:text-[#E63946] transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1rem' }}>
                  {article.title}
                </h4>
                {article.read_time && (
                  <span className="text-zinc-500 text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {article.read_time} min read
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8 sm:hidden">
          <Link to="/blog?category=Peptides" className="btn-stealth text-sm inline-flex items-center gap-2">
            View All Guides <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Trending Now Section
const TrendingNowSection = () => {
  const [trendingArticles, setTrendingArticles] = useState([]);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const res = await articlesApi.getPopular(6);
        setTrendingArticles(res.data || []);
      } catch (e) { console.error(e); }
    };
    fetchTrending();
  }, []);

  if (trendingArticles.length === 0) return null;

  return (
    <section className="section-spacing bg-black" data-testid="trending-now-section">
      <div className="container-ninja">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="text-ninja-label text-[#E63946] mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Trending Now
            </p>
            <h2 className="text-white font-heading">
              What Everyone's Reading
            </h2>
          </div>
          <Link to="/blog" className="btn-stealth text-sm hidden sm:inline-flex items-center gap-2">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {trendingArticles.slice(0, 6).map((article, index) => (
            <Link
              key={article.slug}
              to={`/blogs/article/${article.slug}`}
              className="group flex gap-4 items-start bg-zinc-900/50 border border-zinc-800 hover:border-[#E63946]/40 p-4 transition-all duration-300"
              data-testid={`trending-article-${article.slug}`}
            >
              <span className="text-3xl font-heading text-zinc-700 group-hover:text-[#E63946] transition-colors flex-shrink-0 w-10 text-center" style={{ lineHeight: '1' }}>
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-[#E63946] font-bold">
                    {article.category}
                  </span>
                  {index < 3 && (
                    <span className="flex items-center gap-1 text-[10px] text-orange-400">
                      <Flame className="w-3 h-3" /> Hot
                    </span>
                  )}
                </div>
                <h4 className="text-white text-sm font-medium line-clamp-2 group-hover:text-[#E63946] transition-colors font-heading" style={{ fontSize: '1rem' }}>
                  {article.title}
                </h4>
                <div className="flex items-center gap-3 mt-1.5 text-zinc-500 text-xs">
                  {article.read_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {article.read_time} min
                    </span>
                  )}
                  {article.view_count > 0 && (
                    <span className="flex items-center gap-1">
                      {article.view_count >= 1000 ? `${(article.view_count / 1000).toFixed(1)}k` : article.view_count} views
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8 sm:hidden">
          <Link to="/blog" className="btn-stealth text-sm inline-flex items-center gap-2">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// CTA Section — "Any 3 Tees, $75" merch opt-in (replaced red newsletter form)
const CTASection = () => <ThreeForSeventyFive />;

// Latest Article Banner — hero + up to 2 sub-features (controlled from admin)
const LatestArticleBanner = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await articlesApi.getFeaturedList(3);
        if (res.data && res.data.length > 0) setItems(res.data);
      } catch (e) { console.error(e); }
    };
    fetchFeatured();
  }, []);

  if (items.length === 0) return null;

  const hero = items[0];
  const subs = items.slice(1, 3);
  const hasGrid = subs.length > 0;

  return (
    <section className="relative bg-black overflow-hidden" data-testid="latest-article-banner">
      <div className="absolute inset-0 z-0">
        <img
          src={hero.featured_image}
          alt={hero.title}
          className="w-full h-full object-cover opacity-25"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/60" />
      </div>

      <div className="container-ninja relative z-10 py-12 md:py-16">
        <div className={`grid gap-8 ${hasGrid ? 'lg:grid-cols-[1.6fr_1fr]' : ''}`}>
          {/* HERO */}
          <div className="flex items-start gap-6">
            <div className="hidden md:block w-1 self-stretch bg-[#E63946] flex-shrink-0 rounded-full" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="px-2.5 py-1 text-[10px] uppercase tracking-widest bg-[#E63946] text-white font-bold">
                  Featured
                </span>
                <span className="text-zinc-500 text-xs uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {hero.category}
                </span>
                {hero.created_at && (
                  <span className="text-zinc-600 text-xs">
                    {new Date(hero.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              <Link to={`/blogs/article/${hero.slug}`} className="group block" data-testid="latest-article-link">
                <h3
                  className="text-white text-2xl md:text-3xl lg:text-4xl mb-3 group-hover:text-[#E63946] transition-colors duration-300 line-clamp-3"
                  style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.01em' }}
                >
                  {hero.title}
                </h3>
                {hero.excerpt && (
                  <p className="text-zinc-400 text-sm md:text-base leading-relaxed line-clamp-2 mb-4">
                    {hero.excerpt}
                  </p>
                )}
                <span className="inline-flex items-center gap-2 text-[#E63946] text-sm font-bold uppercase tracking-wider group-hover:gap-3 transition-all duration-300" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  Read Now <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </div>
          </div>

          {/* SUB-FEATURES (only when there are pins beyond the hero) */}
          {hasGrid && (
            <div className="flex flex-col gap-4" data-testid="featured-subs">
              {subs.map((s, i) => (
                <Link
                  key={s.slug}
                  to={`/blogs/article/${s.slug}`}
                  data-testid={`featured-sub-${i}`}
                  className="group flex items-stretch gap-4 p-3 bg-zinc-950/60 border border-zinc-800 hover:border-[#E63946]/60 backdrop-blur-sm transition-all duration-300"
                >
                  {s.featured_image && (
                    <div className="w-24 h-24 flex-shrink-0 overflow-hidden border border-zinc-800">
                      <img
                        src={s.featured_image}
                        alt={s.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <span className="text-[#E63946] text-[9px] uppercase tracking-[0.18em] font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                        {s.category}
                      </span>
                      <h4
                        className="text-white text-base leading-tight mt-1 mb-1 line-clamp-2 group-hover:text-[#E63946] transition-colors"
                        style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.01em' }}
                      >
                        {s.title}
                      </h4>
                    </div>
                    <span className="text-zinc-500 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 group-hover:text-zinc-300 transition-colors">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Continue Reading Section — uses localStorage reading progress
const ContinueReadingSection = () => {
  const { getInProgress, getCompleted } = useReadingProgress();
  const inProgress = getInProgress();
  const completed = getCompleted();

  // Show in-progress first, then recently completed
  const items = [...inProgress.slice(0, 4), ...completed.slice(0, Math.max(0, 4 - inProgress.length))];

  if (items.length === 0) return null;

  return (
    <section className="py-14 md:py-20 bg-[#080808]" data-testid="continue-reading-section">
      <div className="container-ninja">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-ninja-label text-[#00F0FF] mb-2 flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Your Reading
            </p>
            <h2 className="text-white font-heading">
              Continue Where You Left Off
            </h2>
          </div>
          <Link to="/my-results" className="btn-stealth text-sm hidden sm:inline-flex items-center gap-2">
            All History <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((item) => (
            <Link
              key={item.slug}
              to={`/blogs/article/${item.slug}`}
              className="group block bg-zinc-900/40 border border-zinc-800/60 hover:border-[#00F0FF]/30 transition-all duration-300 overflow-hidden"
              data-testid={`continue-reading-${item.slug}`}
            >
              {/* Image */}
              {item.featured_image && (
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img
                    src={item.featured_image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {/* Progress overlay */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800">
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${Math.min(item.progress || 0, 100)}%`,
                        backgroundColor: item.progress >= 95 ? '#22c55e' : '#00F0FF',
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-[#00F0FF] font-bold">
                    {item.category || 'Article'}
                  </span>
                  <span className="text-[10px] text-zinc-600">
                    {item.progress >= 95 ? 'Completed' : `${Math.round(item.progress)}%`}
                  </span>
                </div>
                <h4
                  className="text-white text-sm font-medium line-clamp-2 group-hover:text-[#00F0FF] transition-colors"
                  style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1rem' }}
                >
                  {item.title}
                </h4>
                {item.read_time && (
                  <span className="text-zinc-500 text-xs flex items-center gap-1 mt-1.5">
                    <Clock className="w-3 h-3" /> {item.read_time} min read
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8 sm:hidden">
          <Link to="/my-results" className="btn-stealth text-sm inline-flex items-center gap-2">
            View All History <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Recently Viewed Articles Section
const RecentlyViewedSection = () => {
  const { getRecentlyViewed } = useReadingProgress();
  const items = getRecentlyViewed(6);

  if (items.length === 0) return null;

  return (
    <section className="py-14 md:py-20 bg-[#0a0a0a]" data-testid="recently-viewed-section">
      <div className="container-ninja">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-ninja-label text-[#E63946] mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Recently Viewed
            </p>
            <h2 className="text-white font-heading">Pick Up Where You Left Off</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {items.map((item) => (
            <Link
              key={item.slug}
              to={`/blogs/article/${item.slug}`}
              className="group block bg-zinc-900/40 border border-zinc-800/60 hover:border-[#E63946]/30 transition-all duration-300 overflow-hidden"
              data-testid={`recently-viewed-${item.slug}`}
            >
              {item.featured_image && (
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={item.featured_image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  {item.progress > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800">
                      <div className="h-full" style={{ width: `${Math.min(item.progress || 0, 100)}%`, backgroundColor: item.progress >= 95 ? '#22c55e' : '#E63946' }} />
                    </div>
                  )}
                </div>
              )}
              <div className="p-3">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">{item.category || 'Article'}</span>
                <h4 className="text-white text-xs font-medium line-clamp-2 group-hover:text-[#E63946] transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '0.85rem' }}>
                  {item.title}
                </h4>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// SEO content section for homepage text-to-HTML ratio
const HOME_FAQS = [
  { question: "What is NinjAthlete?", answer: "NinjAthlete is a science-backed performance optimization platform for athletes. We provide evidence-based guides on peptide therapy, HYROX training, biohacking protocols, longevity science, and nutrition — all grounded in peer-reviewed research." },
  { question: "What are peptides and how do they help athletes?", answer: "Peptides are short chains of amino acids that signal specific biological responses. Athletes use peptides like BPC-157 for tissue repair, TB-500 for recovery, and GHK-Cu for anti-aging. NinjAthlete provides dosing calculators, stacking guides, and reconstitution tools backed by clinical research." },
  { question: "What is HYROX and how do I train for it?", answer: "HYROX is a global fitness race combining 8km of running with 8 functional workout stations including SkiErg, sled push/pull, burpee broad jumps, rowing, farmers carry, sandbag lunges, and wall balls. NinjAthlete offers finish time calculators, station split analyzers, race pace tools, and a gym directory of HYROX-ready training facilities." },
  { question: "Are NinjAthlete's tools and calculators free?", answer: "Yes, all NinjAthlete tools are completely free — including our peptide dosing calculator, macro calculator, TDEE calculator, body fat estimator, longevity score assessment, HYROX finish time predictor, and 15+ other performance tools." },
  { question: "What biohacking protocols does NinjAthlete cover?", answer: "NinjAthlete covers cold exposure therapy, red light therapy, breathwork protocols, sleep optimization, nootropics, peptide stacking, HRV training, sauna protocols, and longevity science — all with evidence-based recommendations and interactive tools." },
  { question: "How is NinjAthlete different from other fitness sites?", answer: "NinjAthlete bridges ancient discipline (the ninja mindset of strategic mastery) with modern performance science. Every recommendation includes peer-reviewed citations, interactive calculators, and real protocols tested by our founder — a HYROX competitor and certified performance scientist." },
];

const FAQSection = () => (
  <section className="bg-[#0a0a0a] py-16 border-t border-zinc-900" data-testid="faq-section">
    <div className="container-ninja">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-1 h-6 bg-[#E63946]" />
          <span className="text-[#E63946] text-xs font-bold uppercase tracking-[3px]">FAQ</span>
        </div>
        <h2 className="text-white text-xl font-heading mb-8">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {HOME_FAQS.map((faq, i) => (
            <details key={faq.question} className="group border border-zinc-800 bg-zinc-900/40" data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer px-5 py-4 text-white text-sm font-medium hover:bg-zinc-800/40 transition-colors list-none">
                <span>{faq.question}</span>
                <span className="text-zinc-600 group-open:rotate-45 transition-transform text-lg ml-3">+</span>
              </summary>
              <div className="px-5 pb-4 text-zinc-400 text-sm leading-relaxed border-t border-zinc-800/50 pt-3">
                {faq.answer}
              </div>
            </details>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const SEOContentSection = () => (
  <section className="bg-[#050505] py-16" data-testid="seo-content-section">
    <div className="container-ninja">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-white text-xl font-heading mb-6">Science-Backed Biohacking & Peptide Protocols</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-zinc-400 text-sm leading-relaxed">
          <div>
            <p className="mb-4">
              NinjAthlete is the leading resource for evidence-based biohacking, peptide therapy protocols, and athletic performance optimization. Our comprehensive guides cover BPC-157 dosing, TB-500 stacking protocols, and emerging peptides like Retatrutide for body recomposition.
            </p>
            <p className="mb-4">
              Whether you're training for HYROX competitions, optimizing your recovery with cold exposure and hydrotherapy, or exploring longevity peptides like GHK-Cu and MOTS-c, our science-backed articles and interactive calculators help you make informed decisions about your health and performance.
            </p>
          </div>
          <div>
            <p className="mb-4">
              Use our free fitness tools including the peptide dosing calculator, macro calculator, TDEE calculator, body fat calculator, and peptide reconstitution guide. Track your body recomposition progress, plan peptide cycles, and build custom recovery stacks — all backed by clinical research and real-world protocols.
            </p>
            <p>
              From meditation and mindfulness to cold plunge protocols and breathwork techniques, NinjAthlete bridges ancient wisdom with modern bioscience. Explore our biohacking hub for the latest in wearable tech, sleep optimization, nootropics, and cellular health strategies used by elite athletes worldwide.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Home Page Component
const Home = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await healthApi.check();
        setStatus(response.data);
      } catch (e) {
        console.error('Health check failed:', e);
      }
    };
    checkHealth();
  }, []);

  return (
    <main data-testid="home-page">
      <HomeFAQSchema faqs={HOME_FAQS} />

      {/* CRO Audit §5.3 — homepage funnel sequence */}
      <HeroRedesign />
      <TrustBar />
      <ThreePathsSection />
      <MerchShowcase />
      <LeadMagnetCapture />
      <FeaturedGuideBlock />
      <ToolsStrip />

      {/* Below-fold content kept for SEO + returning-user value */}
      <LatestArticleBanner />
      <EditorsPicksStrip />
      <RecentlyViewedSection />
      <RecommendedForYou />
      <ContinueReadingSection />
      <TrendingNowSection />
      <PeptideGuidesSection />
      <CTASection />
      <RecentlyViewedProductsCarousel />
      <FAQSection />
      <SEOContentSection />

      {/* Exit-intent lead capture — CRO Audit §3.3 #1 */}
      <ExitIntentModal />
      {/* Soft sticky lead-magnet pill — complementary surface, opens same offer */}
      <SoftLeadMagnetGate />
    </main>
  );
};

// Redirect old /blog/:slug URLs to /blogs/article/:slug
function ArticleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/blogs/article/${slug}`} replace />;
}

// Global SEO component — must be inside BrowserRouter
function GlobalSEO() {
  useCanonicalUrl();
  useEffect(() => {
    initAffiliateClickTracker();
  }, []);
  return null;
}

function AppContent() {
  useAffiliateClickTracking();
  const location = window.location.pathname;
  const isAdmin = location === '/admin';

  // CRITICAL: Handle the Emergent OAuth callback synchronously — a session_id
  // in the URL fragment must be processed BEFORE any other route logic runs
  // (including the AuthProvider's /me check). Handles both `/auth/callback`
  // and any URL where Emergent redirected with `#session_id=...`.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
  if (
    typeof window !== 'undefined' &&
    (location === '/auth/callback' || window.location.hash?.includes('session_id='))
  ) {
    return <AuthCallback />;
  }

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navigation />
      <CartDrawer />
      <LastOrderPointsBanner />
      <AIChatWidget />
      <APRPromoPopupGate />
      <PeptiQPopupGate />
      <RouteScrollToTop />
      <SonnerToaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0a0a0a',
            border: '1px solid rgba(199, 250, 62, 0.35)',
            color: '#fff',
          },
        }}
      />
      <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/hyrox" element={<HyroxPage />} />
            <Route path="/run-club-badge/:clubId" element={<RunClubBadgePage />} />
            <Route path="/gym-upload/:token" element={<GymUploadPage />} />
            <Route path="/locator" element={<LocatorHub />} />
            <Route path="/locator/near-me" element={<LocatorNearMe />} />
            <Route path="/locator/submit" element={<LocatorSubmit />} />
            <Route path="/locator/claim/:slug" element={<LocatorClaim />} />
            <Route path="/locator/facility/:slug" element={<LocatorFacilityProfile />} />
            <Route path="/locator/therapy/:therapy" element={<LocatorTherapyHub />} />
            <Route path="/locator/:metro/:therapy" element={<LocatorTherapyMetro />} />
            <Route path="/locator/:slug" element={<LocatorDirectory />} />
            <Route path="/city-series" element={<CitySeriesPage />} />
            <Route path="/womens-collection" element={<WomensCollectionPage />} />
            <Route path="/hyrox/benchmark-report" element={<HyroxBenchmarkReport />} />
            <Route path="/hyrox/near-me" element={<HyroxNearMePage />} />
            <Route path="/hyrox/gyms" element={<HyroxGymsHubPage />} />
            <Route path="/go/:slug" element={<GoRedirect />} />
            <Route path="/gym-preview/:gymId" element={<GymPreviewPage />} />
            <Route path="/partner-kit/:cityKey" element={<PartnerKitPage />} />
            <Route path="/peptide-cheat-sheet" element={<PeptideCheatSheetPage />} />
            <Route path="/peptide-subscription" element={<PeptideSubscriptionPage />} />
            <Route path="/promotion-terms/biohacker-member-gift" element={<PromotionTermsGiftPage />} />
            <Route path="/rewards/biohacker-gift" element={<BiohackerGiftRedemptionPage />} />
            <Route path="/rewards/wallet" element={<BiohackerWalletPage />} />
            <Route path="/gear-box" element={<GearBoxPage />} />
            <Route path="/gear-box/success" element={<GearBoxSuccessPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/blogs/article/:slug" element={<ArticlePage />} />
            <Route path="/blog/:slug" element={<ArticleRedirect />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/:slug" element={<ProductPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/timers" element={<TimersPage />} />
            <Route path="/tools/peptide-calculator" element={<PeptideCalculatorPage />} />
            <Route path="/tools/recovery-payback-calculator" element={<RecoveryCalculatorPage />} />
            <Route path="/tools/protein-calculator" element={<ProteinCalculatorPage />} />
            <Route path="/tools/tdee-calculator" element={<TDEECalculatorPage />} />
            <Route path="/tools/macro-calculator" element={<MacroCalculatorPage />} />
            <Route path="/tools/one-rep-max" element={<OneRepMaxPage />} />
            <Route path="/tools/body-fat-calculator" element={<BodyFatCalculatorPage />} />
            <Route path="/tools/heart-rate-zones" element={<HeartRateZonesPage />} />
            <Route path="/tools/hyrox-finish-time" element={<HyroxFinishTimePage />} />
            <Route path="/tools/peptide-dosing" element={<PeptideDosingCalculatorPage />} />
            <Route path="/tools/station-split" element={<StationSplitCalculatorPage />} />
            <Route path="/tools/race-pace" element={<StationSplitCalculatorPage />} />
            <Route path="/my-results" element={<MyResultsPage />} />
            <Route path="/affiliates" element={<AffiliatePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/hydrotherapy" element={<HydrotherapyPage />} />
            <Route path="/tools/recovery-stack" element={<RecoveryStackPage />} />
            <Route path="/tools/reconstitution-guide" element={<ReconstitutionGuidePage />} />
            <Route path="/tools/body-recomp" element={<BodyRecompPage />} />
            <Route path="/tools/peptide-cycle" element={<PeptideCycleTrackerPage />} />
            <Route path="/tools/peptide-stack" element={<PeptideStackBuilderPage />} />
            <Route path="/tools/peptide-stack-builder" element={<PeptideStackBuilderPage />} />
            <Route path="/tools/meal-planner" element={<MealPlannerPage />} />
            <Route path="/tools/macro-tracker" element={<MacroTrackerPage />} />
            <Route path="/affiliate" element={<AffiliatePortalPage />} />
            <Route path="/tools/peptide-compare" element={<PeptideComparisonToolPage />} />
            <Route path="/tools/protocol-tracker" element={<ProtocolTrackerPage />} />
            <Route path="/guides" element={<GuidesHubPage />} />
            <Route path="/biohacking" element={<BiohackingHubPage />} />
            <Route path="/tools/biohacking-score" element={<BiohackingScorePage />} />
            <Route path="/tools/longevity-score" element={<LongevityScorePage />} />
            <Route path="/peptides" element={<PeptidesHubPage />} />
            <Route path="/recovery" element={<RecoveryHubPage />} />
            <Route path="/longevity" element={<LongevityHubPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/returns" element={<ReturnsPage />} />
            <Route path="/shipping" element={<ShippingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/about/daniel-rivas" element={<AuthorPage />} />
            <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/profile/:userId" element={<PublicProfilePage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/race-plan/:shareToken" element={<SharedRacePlanPage />} />
            <Route path="/hyrox-content" element={<HyroxContentPage />} />
            <Route path="/affiliates/leaderboard" element={<AffiliateLeaderboardPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/community/challenges" element={<ChallengesPage />} />
            <Route path="/community/challenge/:challengeId" element={<ChallengeDetailPage />} />
            <Route path="/community/rewards" element={<RewardsPage />} />
            <Route path="/gym-directory" element={<GymDirectoryPage />} />
            <Route path="/coming-soon" element={<ComingSoonPage />} />
            <Route path="/brand-guidelines" element={<BrandGuidelinesPage />} />
            <Route path="/series" element={<SeriesPage />} />
            <Route path="/series/:slug" element={<SeriesPage />} />
            <Route path="/community/:username" element={<CommunityProfilePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Footer />
        </div>
  );
}

function App() {
  return (
    <AuthProvider>
    <SavedProvider>
    <CartProvider>
      <BrowserRouter>
        <GlobalSEO />
        <AppContent />
      </BrowserRouter>
    </CartProvider>
    </SavedProvider>
    </AuthProvider>
  );
}

export default App;
